import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Sample, type NutrientAlternativeGroup } from '../../graphql/api/types'
import type { AnalysisUploadSource } from './types'

export type { AnalysisUploadSource } from './types'
export { UPLOAD_SOURCE } from './types'

type UploadedAnalysesPayload = {
  readonly samples: ReadonlyArray<Sample>
  readonly uploadSource: AnalysisUploadSource
  /**
   * From `startPdfIngestion`: nutrients per sample row where extraction JSON
   * listed `alternatives`. Rows are dropped as the user resolves each nutrient for the batch.
   */
  readonly nutrientAlternativeGroupsBySampleIndex?: ReadonlyArray<ReadonlyArray<NutrientAlternativeGroup>> | null
  /** ID of the async `PdfIngestionJob` that produced these samples, if any. Passed to `createManyAnalyses`. */
  readonly ingestionJobId?: string | null
  /** Present when `uploadSource === 'ai_pdf'` and DIARC returned an analyses-file id. */
  readonly diarcAnalysesFileId?: string | null
  /** Stringified DIARC extraction envelope from parse; required to PATCH VALIDATED. */
  readonly diarcExtractionEnvelopeJson?: string | null
  /** Alignment warnings from review draft / ProcessedLaboratoryFile (array or stringified JSON). */
  readonly aiPdfAlignmentWarnings?: unknown
  /** Set when this upload should create one farm field per sample. */
  readonly soilZoneFarmId?: string | null
  /** Where to go after those fields are saved. */
  readonly soilZoneReturnTo?: 'zones' | 'hierarchy' | null
}

export type SoilZoneCrop = {
  readonly farmId: string
  readonly cropRegionId: string
}

export const analysesSlice = createSlice({
  name: 'analyses',
  initialState: {
    uploaded: [] as Sample[],
    uploadSource: null as AnalysisUploadSource | null,
    nutrientAlternativeGroupsBySampleIndex: null as UploadedAnalysesPayload['nutrientAlternativeGroupsBySampleIndex'],
    ingestionJobId: null as string | null,
    diarcAnalysesFileId: null as string | null,
    diarcExtractionEnvelopeJson: null as string | null,
    aiPdfAlignmentWarnings: null as unknown,
    soilZoneFarmId: null as string | null,
    soilZoneReturnTo: null as 'zones' | 'hierarchy' | null,
    soilZoneCrop: null as SoilZoneCrop | null,
  },
  reducers: {
    setUploadedAnalyses: (state, action: PayloadAction<UploadedAnalysesPayload>) => {
      state.uploaded = [...action.payload.samples]
      state.uploadSource = action.payload.uploadSource
      state.nutrientAlternativeGroupsBySampleIndex =
        action.payload.uploadSource === 'ai_pdf'
          ? action.payload.nutrientAlternativeGroupsBySampleIndex != null
            ? action.payload.nutrientAlternativeGroupsBySampleIndex.map((row) => [...row])
            : null
          : null
      state.ingestionJobId = action.payload.uploadSource === 'ai_pdf' ? action.payload.ingestionJobId ?? null : null
      if (action.payload.uploadSource === 'spreadsheet') {
        state.diarcAnalysesFileId = null
        state.diarcExtractionEnvelopeJson = null
        state.aiPdfAlignmentWarnings = null
      } else {
        if (action.payload.diarcAnalysesFileId !== undefined) {
          state.diarcAnalysesFileId = action.payload.diarcAnalysesFileId
        }
        if (action.payload.diarcExtractionEnvelopeJson !== undefined) {
          state.diarcExtractionEnvelopeJson = action.payload.diarcExtractionEnvelopeJson
        }
        // Always replace so a new review without warnings does not keep a prior upload's list.
        state.aiPdfAlignmentWarnings = action.payload.aiPdfAlignmentWarnings ?? null
      }
      state.soilZoneFarmId = action.payload.soilZoneFarmId ?? null
      state.soilZoneReturnTo = action.payload.soilZoneFarmId ? action.payload.soilZoneReturnTo ?? 'zones' : null
    },
    setSoilZoneCrop: (state, action: PayloadAction<SoilZoneCrop | null>) => {
      state.soilZoneCrop = action.payload
    },
    clearUploadedAnalyses: (state) => {
      state.uploaded = []
      state.uploadSource = null
      state.nutrientAlternativeGroupsBySampleIndex = null
      state.ingestionJobId = null
      state.diarcAnalysesFileId = null
      state.diarcExtractionEnvelopeJson = null
      state.aiPdfAlignmentWarnings = null
      state.soilZoneFarmId = null
      state.soilZoneReturnTo = null
    },
  },
})

export const { setUploadedAnalyses, setSoilZoneCrop, clearUploadedAnalyses } = analysesSlice.actions
